
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Property } from "@/types"
import { 
  getCurrencySymbol as getCurrencySymbolUtil, 
  getUserCurrency as getUserCurrencyUtil, 
  formatCurrency as formatCurrencyUtil 
} from "@/utils/currency"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Calculate the Return on Investment (ROI) for a property
 * ROI = (Net Annual Income / Total Cash Invested) × 100
 */
export function calculateROI(property: Property): number {
  // Calculate annual rent
  const annualRent = property.rent.amount * 12;
  
  // Calculate annual mortgage payment
  const annualMortgage = property.mortgage.monthlyPayment * 12;
  
  // Get annual expenses (use provided value or default to insurance cost)
  const annualExpenses = 
    property.investment?.annualExpenses || 
    property.insurance.cost || 0;
  
  // Calculate net annual income
  const netAnnualIncome = annualRent - annualMortgage - annualExpenses;
  
  // Calculate total cash invested 
  // (deposit + refurb costs + stamp duty + legal fees + other costs)
  const totalCashInvested = 
    (property.investment?.deposit || 0) + 
    (property.investment?.refurbCosts || 0) +
    (property.investment?.stampDuty || 0) +
    (property.investment?.legalFees || 0) +
    (property.investment?.otherCosts || 0);
  
  // If no investment data available, use 25% of purchase price as a fallback
  const fallbackInvestment = totalCashInvested === 0 ? property.purchasePrice * 0.25 : totalCashInvested;
  
  // Calculate ROI percentage
  // Guard against division by zero
  if (fallbackInvestment === 0) return 0;
  
  const roi = (netAnnualIncome / fallbackInvestment) * 100;
  
  return roi;
}

/**
 * Format a number as a percentage with specified decimal places
 */
export function formatPercentage(value: number, decimalPlaces: number = 2): string {
  return `${value.toFixed(decimalPlaces)}%`;
}

/**
 * Get the user's preferred currency code
 */
export function getUserCurrency(userId?: string): string {
  return getUserCurrencyUtil(userId);
}

/**
 * Get the currency symbol based on currency code
 */
export function getCurrencySymbol(userId?: string): string {
  return getCurrencySymbolUtil(userId);
}

/**
 * Format a number as currency using the user's preferred currency
 */
export function formatCurrency(value: number, userId?: string): string {
  return formatCurrencyUtil(value, userId);
}
